import { useState, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Avatar,
  Grid,
  Typography,
} from '@mui/material'
import { PhotoCamera } from '@mui/icons-material'
import type { FamilyMember, FamilyMemberFormData, AgeCategory } from '@/types'
import { DEFAULT_COLORS } from '@/types'
import { useFamilyStore } from '@/store/useFamilyStore'

interface FamilyMemberFormProps {
  open: boolean
  onClose: () => void
  member?: FamilyMember | null
}

export default function FamilyMemberForm({
  open,
  onClose,
  member,
}: FamilyMemberFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const addMember = useFamilyStore((state) => state.addMember)
  const updateMember = useFamilyStore((state) => state.updateMember)
  const uploadAvatar = useFamilyStore((state) => state.uploadAvatar)

  const [formData, setFormData] = useState<FamilyMemberFormData>({
    name: member?.name || '',
    avatar_url: member?.avatar_url || null,
    color: member?.color || DEFAULT_COLORS[0],
    age_category: member?.age_category || 'Adult',
    notification_sound: member?.notification_sound || 'default',
  })

  const [uploading, setUploading] = useState(false)

  const handleChange = (field: keyof FamilyMemberFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    const url = await uploadAvatar(file)
    if (url) {
      setFormData((prev) => ({ ...prev, avatar_url: url }))
    }
    setUploading(false)
  }

  const handleSubmit = async () => {
    if (member) {
      await updateMember(member.id, formData)
    } else {
      await addMember(formData)
    }
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          fontWeight: 700,
        }}
      >
        {member ? 'Edit' : 'Add'} Family Member
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3} mt={3}>
          {/* Avatar Upload */}
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap={2}
            sx={{
              p: 3,
              bgcolor: 'rgba(99, 102, 241, 0.05)',
              borderRadius: 3,
              border: '2px dashed',
              borderColor: 'primary.main',
            }}
          >
            <Avatar
              src={formData.avatar_url || undefined}
              sx={{
                width: 100,
                height: 100,
                bgcolor: formData.color,
                fontSize: '2.5rem',
                border: '4px solid white',
                boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
              }}
            >
              {formData.name[0] || '?'}
            </Avatar>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleAvatarUpload}
            />
            <Button
              variant="outlined"
              startIcon={<PhotoCamera />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              sx={{
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                },
              }}
            >
              {uploading ? 'Uploading...' : 'Upload Photo'}
            </Button>
          </Box>

          {/* Name */}
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            fullWidth
            placeholder="Enter family member's name"
          />

          {/* Age Category */}
          <FormControl fullWidth>
            <InputLabel>Age Category</InputLabel>
            <Select
              value={formData.age_category}
              label="Age Category"
              onChange={(e) =>
                handleChange('age_category', e.target.value as AgeCategory)
              }
            >
              <MenuItem value="Adult">👨 Adult</MenuItem>
              <MenuItem value="Teen">🧒 Teen</MenuItem>
              <MenuItem value="Child">👶 Child</MenuItem>
            </Select>
          </FormControl>

          {/* Color Selection */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                mb: 2,
              }}
            >
              Theme Color
            </Typography>
            <Grid container spacing={1.5}>
              {DEFAULT_COLORS.map((color) => (
                <Grid item key={color}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: color,
                      borderRadius: 2,
                      cursor: 'pointer',
                      border: '3px solid',
                      borderColor:
                        formData.color === color ? color : 'transparent',
                      boxShadow:
                        formData.color === color
                          ? `0 0 0 3px white, 0 0 0 6px ${color}`
                          : '0 2px 4px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: `0 4px 12px ${color}66`,
                      },
                    }}
                    onClick={() => handleChange('color', color)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} size="large">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.name || uploading}
          size="large"
          sx={{
            minWidth: 120,
          }}
        >
          {member ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
